import { useId, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * A labelled form field with an inline error.
 *
 * Errors are shown under the offending input and wired through
 * `aria-describedby`, never as a toast: a toast is dismissed before the user
 * reaches the field it refers to, and on a five-step wizard that means being
 * told "something is wrong" with no way back to what.
 *
 * The id is generated here and handed to the child through a render prop, so a
 * caller cannot forget to tie the label to the control.
 */
export function Field({
  label,
  hint,
  error,
  optional,
  className,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** The design marks optional fields, never required ones. */
  optional?: boolean;
  className?: string;
  children: (props: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": boolean | undefined;
  }) => ReactNode;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("flex flex-col gap-[7px]", className)}>
      <label
        htmlFor={id}
        className="font-display text-[0.72rem] font-semibold uppercase tracking-[0.09em] text-ink-slate"
      >
        {label}
        {optional && <span className="normal-case"> (optional)</span>}
      </label>

      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
      })}

      {hint && !error && (
        <p id={hintId} className="text-[0.8rem] leading-[1.5] text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-[0.8rem] font-semibold leading-[1.5] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
